/**
 * Mirror of `arbitrage-core/packages/matching-core/src/taxonomy.ts` —
 * the four codebases (arbitrage-core / exchange-connectors / prediction-connectors /
 * monitor-crypto-react) don't share a workspace, so we duplicate the closed
 * lists here rather than reach across repo boundaries.
 *
 * Keep in sync with TAXONOMY_VERSION there. Drift = silently dropped chips.
 */
import {
  PredictionCategory,
  UNCATEGORIZED,
} from './categories';

const SPORTS = [
  'soccer',
  'basketball',
  'baseball',
  'football',
  'hockey',
  'golf',
  'mma',
  'tennis',
  'motorsport',
  'esports',
  'boxing',
  'rugby',
  'cricket',
  'other_sport',
] as const;

const CRYPTO = ['coin', 'other_crypto'] as const;

const POLITICS = [
  'us_policy',
  'us_scotus',
  'us_congress',
  'foreign_policy',
  'international',
  'other_politics',
] as const;

const ELECTIONS = [
  'us_house',
  'us_senate',
  'us_presidential',
  'us_governor',
  'us_primary',
  'foreign_leader',
  'foreign_election',
  'other_election',
] as const;

const CLIMATE = [
  'temperature',
  'daily_high_temp',
  'daily_low_temp',
  'monthly_precipitation',
  'monthly_snowfall',
  'precipitation',
  'hottest_month',
  'hottest_year',
  'coldest_month',
  'coldest_year',
  'tornado',
  'hurricane',
  'earthquake',
  'volcanic',
  'sea_ice',
  'global_temp',
  'climate_policy',
  'wildfire',
  'other_climate',
] as const;

const ECONOMICS = [
  'fed_rate',
  'gdp',
  'cpi',
  'employment',
  'housing',
  'oil',
  'other_econ',
] as const;

const FINANCIALS = [
  'stock',
  'index',
  'forex',
  'metals',
  'treasuries',
  'other_fin',
] as const;

const ENTERTAINMENT = [
  'oscars',
  'emmys',
  'grammys',
  'box_office',
  'tv',
  'music',
  'social_media',
  'other_ent',
] as const;

const MENTIONS = [
  'politicians',
  'earnings',
  'sports_announcers',
  'other_speaker',
] as const;

const OTHER = ['other'] as const;

export const SUBCATEGORIES_BY_CATEGORY: Record<
  PredictionCategory,
  readonly string[]
> = {
  sports: SPORTS,
  crypto: CRYPTO,
  politics: POLITICS,
  elections: ELECTIONS,
  economics: ECONOMICS,
  financials: FINANCIALS,
  entertainment: ENTERTAINMENT,
  climate_weather: CLIMATE,
  mentions: MENTIONS,
  other: OTHER,
  [UNCATEGORIZED]: [UNCATEGORIZED],
};

const HUMANIZE: Record<string, string> = {
  // Generic
  uncategorized: 'Uncategorized',
  other: 'Other',
  other_sport: 'Other Sport',
  other_crypto: 'Other Crypto',
  other_politics: 'Other Politics',
  other_election: 'Other Election',
  other_econ: 'Other Economics',
  other_fin: 'Other Financial',
  other_ent: 'Other Entertainment',
  other_climate: 'Other Climate',
  other_speaker: 'Other Speaker',

  // Sports
  soccer: 'Soccer',
  basketball: 'Basketball',
  baseball: 'Baseball',
  football: 'Football',
  hockey: 'Hockey',
  golf: 'Golf',
  mma: 'MMA',
  tennis: 'Tennis',
  motorsport: 'Motorsport',
  esports: 'Esports',
  boxing: 'Boxing',
  rugby: 'Rugby',
  cricket: 'Cricket',

  // Crypto
  coin: 'Coin',

  // Politics
  us_policy: 'US Policy',
  us_scotus: 'US SCOTUS',
  us_congress: 'US Congress',
  foreign_policy: 'Foreign Policy',
  international: 'International',

  // Elections
  us_house: 'US House',
  us_senate: 'US Senate',
  us_presidential: 'US Presidential',
  us_governor: 'US Governor',
  us_primary: 'US Primary',
  foreign_leader: 'Foreign Leader',
  foreign_election: 'Foreign Election',

  // Climate
  temperature: 'Temperature',
  daily_high_temp: 'Daily High Temp',
  daily_low_temp: 'Daily Low Temp',
  monthly_precipitation: 'Monthly Precipitation',
  monthly_snowfall: 'Monthly Snowfall',
  precipitation: 'Precipitation',
  hottest_month: 'Hottest Month',
  hottest_year: 'Hottest Year',
  coldest_month: 'Coldest Month',
  coldest_year: 'Coldest Year',
  tornado: 'Tornado',
  hurricane: 'Hurricane',
  earthquake: 'Earthquake',
  volcanic: 'Volcanic',
  sea_ice: 'Sea Ice',
  global_temp: 'Global Temp',
  climate_policy: 'Climate Policy',
  wildfire: 'Wildfire',

  // Economics
  fed_rate: 'Fed Rate',
  gdp: 'GDP',
  cpi: 'CPI',
  employment: 'Employment',
  housing: 'Housing',
  oil: 'Oil',

  // Financials
  stock: 'Stock',
  index: 'Index',
  forex: 'Forex',
  metals: 'Metals',
  treasuries: 'Treasuries',

  // Entertainment
  oscars: 'Oscars',
  emmys: 'Emmys',
  grammys: 'Grammys',
  box_office: 'Box Office',
  tv: 'TV',
  music: 'Music',
  social_media: 'Social Media',

  // Mentions
  politicians: 'Politicians',
  earnings: 'Earnings',
  sports_announcers: 'Sports Announcers',
};

export function subcategoryLabel(sub: string | undefined | null): string {
  if (!sub) return 'Uncategorized';
  return HUMANIZE[sub] ?? sub.replace(/_/g, ' ');
}
